"use client";
import { I18nUtil } from "@utils/i18n";
import Translations from "@translate/components/ChatModal/ThemePackChatHandoff";

import { useEffect, useRef, useState } from "react";
import {
  isSyncStatusDone,
  resolveSyncGateView,
  useFinancialSyncStatus,
} from "@hooks/financialSyncStatus/useFinancialSyncStatus";
import { FINANCIAL_SYNC_WAIT_BUDGET_MS } from "@hooks/financialSyncStatus/syncStatusView";

import { Track } from "@/src/lib/tracking";
import ErrorTranslations from "@translate/screens/theme-pack/ThemePackConnectedPage";
const ErrorI18n = I18nUtil.createScoped(ErrorTranslations);
import ContinueTranslations from "@translate/components/OnboardingWizard/shared/OnboardingCtaButton";
const ContinueI18n = I18nUtil.createScoped(ContinueTranslations);

const I18n = I18nUtil.createScoped(Translations);

interface Props {
  themeId: string;
  chatId: string;
  onReady: () => Promise<boolean | { sent: boolean; reason?: string }>;
}

function HandoffAttempt({
  onReady,
  onRetry,
  themeId,
  chatId,
  attempt,
}: Props & { onRetry: () => void; attempt: number }) {
  const [phase, setPhase] = useState<
    "waiting" | "submitting" | "sent" | "failed"
  >("waiting");
  const [sendFailed, setSendFailed] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const submitted = useRef(false);
  const { status } = useFinancialSyncStatus({
    enabled: phase === "waiting" && !timedOut,
  });
  const gate = resolveSyncGateView(status);
  const ready =
    gate.kind === "invest-done" ||
    (gate.kind === "unknown" && isSyncStatusDone(status));
  const failed = gate.kind === "no-invest" || gate.kind === "invest-failed";

  useEffect(() => {
    const timer = window.setTimeout(
      () => setTimedOut(true),
      FINANCIAL_SYNC_WAIT_BUDGET_MS,
    );
    return () => window.clearTimeout(timer);
  }, []);

  const submit = (trigger: "automatic" | "continue") => {
    const syncState = gate.kind;
    if (submitted.current) {
      return;
    }
    submitted.current = true;
    setPhase("submitting");
    void onReady().then(
      (result) => {
        const sent = typeof result === "boolean" ? result : result.sent;
        submitted.current = sent;
        setSendFailed(!sent);
        setPhase(sent ? "sent" : "failed");
        Track.event("pack_chat_handoff_result", {
          theme_id: themeId,
          chat_id: chatId,
          attempt,
          trigger,
          sync_state: syncState,
          result: sent ? "sent" : "blocked",
          reason: typeof result === "boolean" ? "unknown" : result.reason,
        });
      },
      (error) => {
        submitted.current = false;
        setSendFailed(true);
        setPhase("failed");
        Track.error(error, { tag: "ThemePackChatHandoff", msg: "send failed" });
        Track.event("pack_chat_handoff_result", {
          theme_id: themeId,
          chat_id: chatId,
          attempt,
          trigger,
          sync_state: syncState,
          result: "error",
          reason: "send_failed",
        });
      },
    );
  };

  useEffect(() => {
    if (phase !== "waiting" || submitted.current) {
      return;
    }
    if (failed || (timedOut && !ready)) {
      setPhase("failed");
      Track.event("pack_chat_handoff_result", {
        theme_id: themeId,
        chat_id: chatId,
        attempt,
        trigger: "automatic",
        sync_state: gate.kind,
        result: "not_ready",
        reason: failed ? gate.kind : "timeout",
      });
      return;
    }
    if (!ready) {
      return;
    }
    submit("automatic");
  }, [failed, onReady, phase, ready, timedOut]);

  if (phase === "sent") {
    return null;
  }
  return (
    <div
      className="mx-auto mt-8 flex w-full max-w-[900px] flex-col gap-3 rounded-3 border border-border_solid bg-background_solid_neutral_primary p-4 text-content_primary"
      role="status"
      aria-live="polite"
    >
      <p className="body_medium_m">
        {phase === "failed"
          ? sendFailed
            ? ErrorI18n.t("Couldn’t open Ed. Please try again.")
            : I18n.t("I couldn’t read your holdings yet.")
          : I18n.t("Reading your holdings…")}
      </p>
      {!sendFailed && (
        <p className="body_s text-content_secondary">
          {phase === "failed"
            ? I18n.t("Check your brokerage connection, then try again.")
            : I18n.t(
                "I’ll compare what you hold with your ranking as soon as the account is ready.",
              )}
        </p>
      )}
      {phase === "waiting" || phase === "failed" ? (
        <button
          type="button"
          data-openreplay-label="pack_chat_handoff_continue"
          className="body_medium_s self-start text-content_brand_primary"
          onClick={() => submit("continue")}
        >
          {ContinueI18n.t("Continue")}
        </button>
      ) : null}
      {phase === "failed" ? (
        <button
          type="button"
          data-openreplay-label="pack_chat_handoff_retry"
          className="body_medium_s self-start text-content_brand_primary"
          onClick={onRetry}
        >
          {I18n.t("Try again")}
        </button>
      ) : null}
    </div>
  );
}

export function ThemePackChatHandoff({ onReady, themeId, chatId }: Props) {
  const [attempt, setAttempt] = useState(0);
  return (
    <HandoffAttempt
      themeId={themeId}
      chatId={chatId}
      attempt={attempt + 1}
      key={`${chatId}:${attempt}`}
      onReady={onReady}
      onRetry={() => setAttempt((value) => value + 1)}
    />
  );
}
